import React, { Component, Suspense, useState } from 'react';

export type NavigationShellPlugin = {
    id: string;
    label: string;
    component?: React.ComponentType;
    load?: () => Promise<{ default: React.ComponentType }>;
    loadingFallback?: React.ReactNode;
    unavailableFallback?: React.ReactNode;
    onError?: (error: Error) => void;
};

type PluginErrorBoundaryProps = {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    onError?: (error: Error) => void;
};

type PluginErrorBoundaryState = {
    failed: boolean;
};

class PluginErrorBoundary extends Component<
    PluginErrorBoundaryProps,
    PluginErrorBoundaryState
> {
    state: PluginErrorBoundaryState = {
        failed: false,
    };

    static getDerivedStateFromError(): PluginErrorBoundaryState {
        return { failed: true };
    }

    componentDidCatch(error: Error) {
        this.props.onError?.(error);
    }

    render() {
        if (this.state.failed) {
            return this.props.fallback ?? null;
        }

        return this.props.children;
    }
}

const PluginRenderer = ({ plugin }: { plugin: NavigationShellPlugin }) => {
    const [LazyPlugin] = useState(() =>
        plugin.load ? React.lazy(plugin.load) : null,
    );

    if (plugin.component) {
        const Plugin = plugin.component;
        return <Plugin />;
    }

    if (!LazyPlugin) {
        return null;
    }

    return (
        <Suspense fallback={plugin.loadingFallback ?? null}>
            <LazyPlugin />
        </Suspense>
    );
};

export const NavigationPluginSlot = ({
    plugin,
}: {
    plugin: NavigationShellPlugin;
}) => (
    <PluginErrorBoundary
        fallback={plugin.unavailableFallback}
        onError={plugin.onError}
    >
        <div
            className="opus-navigation-shell__plugin"
            role="group"
            aria-label={plugin.label}
            data-shell-plugin={plugin.id}
        >
            <PluginRenderer plugin={plugin} />
        </div>
    </PluginErrorBoundary>
);
